import React from 'react';
import { MapChartConfig } from '../config/mapChartTypes';

interface MapChartConfigPanelProps {
    config: MapChartConfig;
    onChange: (newConfig: MapChartConfig) => void;
}

export const MapChartConfigPanel: React.FC<MapChartConfigPanelProps> = ({ config, onChange }) => {
    const handleChange = (key: keyof MapChartConfig, value: any) => {
        onChange({ ...config, [key]: value });
    };

    return (
        <div className="flex flex-col gap-4 p-4 text-sm text-gray-200 overflow-y-auto h-full">
            <h3 className="text-gold font-bold uppercase tracking-wider mb-2">Map Settings</h3>

            {/* Mode Selection */}
            <div className="flex flex-col gap-2">
                <label className="text-xs text-gray-400">Display Mode</label>
                <select
                    value={config.mode}
                    onChange={(e) => handleChange('mode', e.target.value)}
                    className="bg-black/20 border border-white/10 rounded p-2 text-white focus:border-gold outline-none"
                >
                    <option value="choropleth">Choropleth (Regions)</option>
                    <option value="bubbles">Bubbles (Points)</option>
                    <option value="combined">Combined</option>
                </select>
            </div>

            {/* Visual Settings */}
            <div className="flex flex-col gap-2">
                <label className="text-xs text-gray-400">Color Mode</label>
                <select
                    value={config.colorMode}
                    onChange={(e) => handleChange('colorMode', e.target.value)}
                    className="bg-black/20 border border-white/10 rounded p-2 text-white focus:border-gold outline-none"
                >
                    <option value="value">By Value (Gradient)</option>
                    <option value="category">By Category</option>
                </select>
            </div>

            {/* Bubble Settings */}
            {(config.mode === 'bubbles' || config.mode === 'combined') && (
                <div className="p-3 bg-white/5 rounded-lg flex flex-col gap-3">
                    <div className="text-xs font-bold text-gray-400">Bubble Settings</div>

                    <div className="flex flex-col gap-1">
                        <div className="flex justify-between text-xs">
                            <span>Min Radius</span>
                            <span>{config.minBubbleRadius}px</span>
                        </div>
                        <input
                            type="range"
                            min="1"
                            max="20"
                            value={config.minBubbleRadius}
                            onChange={(e) => handleChange('minBubbleRadius', Number(e.target.value))}
                            className="accent-gold"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <div className="flex justify-between text-xs">
                            <span>Max Radius</span>
                            <span>{config.maxBubbleRadius}px</span>
                        </div>
                        <input
                            type="range"
                            min="5"
                            max="100"
                            value={config.maxBubbleRadius}
                            onChange={(e) => handleChange('maxBubbleRadius', Number(e.target.value))}
                            className="accent-gold"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <div className="flex justify-between text-xs">
                            <span>Opacity</span>
                            <span>{config.bubbleOpacity}</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={config.bubbleOpacity}
                            onChange={(e) => handleChange('bubbleOpacity', Number(e.target.value))}
                            className="accent-gold"
                        />
                    </div>
                </div>
            )}

            {/* Region Settings */}
            {(config.mode === 'choropleth' || config.mode === 'combined') && (
                <div className="p-3 bg-white/5 rounded-lg flex flex-col gap-3">
                    <div className="text-xs font-bold text-gray-400">Region Settings</div>

                    <div className="flex flex-col gap-1">
                        <div className="flex justify-between text-xs">
                            <span>Opacity</span>
                            <span>{config.regionOpacity}</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={config.regionOpacity}
                            onChange={(e) => handleChange('regionOpacity', Number(e.target.value))}
                            className="accent-gold"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <div className="flex justify-between text-xs">
                            <span>Border Width</span>
                            <span>{config.regionBorderWidth}px</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="5"
                            step="0.5"
                            value={config.regionBorderWidth}
                            onChange={(e) => handleChange('regionBorderWidth', Number(e.target.value))}
                            className="accent-gold"
                        />
                    </div>
                </div>
            )}

            {/* Map Controls */}
            <div className="p-3 bg-white/5 rounded-lg flex flex-col gap-3">
                <div className="text-xs font-bold text-gray-400">Map Controls</div>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                        type="checkbox"
                        checked={config.showZoomControls}
                        onChange={(e) => handleChange('showZoomControls', e.target.checked)}
                        className="accent-gold"
                    />
                    Show Zoom Controls
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                        type="checkbox"
                        checked={config.showScaleBar}
                        onChange={(e) => handleChange('showScaleBar', e.target.checked)}
                        className="accent-gold"
                    />
                    Show Scale Bar
                </label>
            </div>

        </div>
    );
};
